import React from 'react';
import { useMainEntityName, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { useMainTimePeriodShortDisplayName, useComparisonTimePeriodShortDisplayName } from 'src/utils/Hooks/typedHooks';
import { useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { capitalize } from 'lodash';
import {
  VariationsFilter,
  VariationsGaugeData,
  VariationsPerformanceGaugeData
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/contentVariationUtils';
import { ContentSectionId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/ContentSectionId';
import { useScrollToId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useScrollToId';
import { useVariationsFilterStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/stores/VariationsFilterStore';
import TextArrow from '../components/TextArrow';

const mask1 = '***';

export const useDynamicVariationsSubtitles = () => {
  const entityName = useMainEntityName();
  const entityType = useMainEntityType();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const metricFormatter = useMetricFormatter();
  const scrollToId = useScrollToId<typeof ContentSectionId>();
  const {
    setIsSingleSkuPagesEnabled,
    setIsVariatedSkuPagesEnabled,
    setIsCorrectChildSkusEnabled,
    setIsIncorrectChildSkusEnabled,
    setIsInaccurateParentPagesEnabled,
    resetFilters
  } = useVariationsFilterStore();

  const getEntityDisplayName = () => {
    switch (entityType) {
      case 'product':
        return 'this product';
      case 'category':
      case 'subcategory':
        return `the ${entityName} ${entityType.toLowerCase()}`;
      default:
        return entityName;
    }
  };

  const displayName = getEntityDisplayName();

  const ClientLevelVariationsTileSubtitles = {
    [VariationsFilter.ChildVariationRate]: `${mask1} (of ${mask1}) child SKUs are variated on parent pages.`,
    [VariationsFilter.SingleSkuPages]: `View SKUs without parent pages.`,
    [VariationsFilter.VariatedSkuPages]: `View SKUs variated on parent pages.`,
    [VariationsFilter.ChildAccuracy]: `${mask1} (of ${mask1}) child SKUs are variated on the correct parent pages.`,
    [VariationsFilter.IncorrectChildSkus]: `View SKUs with inaccurate variations.`,
    [VariationsFilter.CorrectChildSkus]: `View SKUs with accurate variations.`,
    [VariationsFilter.ParentPageAccuracy]: `${mask1} (of ${mask1}) parent pages have accurate child SKU variations.`,
    [VariationsFilter.InaccurateParentPages]: `View pages with inaccurate variations.`,
    [VariationsFilter.PageDensity]: `There are ${mask1} pages versus a target of ${mask1}.`
  };

  const generateSingleMetricSubtitle = (item: VariationsGaugeData, filterKey: VariationsFilter) => {
    const formatType = item.valueMetricFormatter?.valueMetricType || METRICTYPE.INTEGER;
    const decimalPlaces = item.valueMetricFormatter?.decimalPlaces || 0;

    const formattedValue = metricFormatter(item.value, formatType, {
      showFullValue: false,
      decimalPlaces
    });

    const template = ClientLevelVariationsTileSubtitles[filterKey];

    // Check if we have numerator and denominator for the specific metrics
    if (item.numerator !== undefined && item.denominator !== undefined) {
      const formattedNumerator = metricFormatter(item.numerator, METRICTYPE.INTEGER, {
        showFullValue: false,
        decimalPlaces: 1
      });
      const formattedDenominator = metricFormatter(item.denominator, METRICTYPE.INTEGER, {
        showFullValue: false,
        decimalPlaces: 1
      });

      return template.replace(mask1, formattedNumerator).replace(mask1, formattedDenominator);
    }

    return template.replace(mask1, formattedValue);
  };

  const generateProductTableFilterSubtitle = (volume: number, filterKey: VariationsFilter) => {
    const volumeMetric = metricFormatter(volume, METRICTYPE.VOLUME, {
      showFullValue: false,
      decimalPlaces: 1
    });

    const subText = ClientLevelVariationsTileSubtitles[filterKey].replace(mask1, volumeMetric);

    return (
      <TextArrow
        onClick={async () => {
          await scrollToId(ContentSectionId.TableGrid);
          resetFilters();
          if (filterKey === VariationsFilter.SingleSkuPages) {
            setIsSingleSkuPagesEnabled(true);
          } else if (filterKey === VariationsFilter.IncorrectChildSkus) {
            setIsIncorrectChildSkusEnabled(true);
          } else if (filterKey === VariationsFilter.VariatedSkuPages) {
            setIsVariatedSkuPagesEnabled(true);
          } else if (filterKey === VariationsFilter.CorrectChildSkus) {
            setIsCorrectChildSkusEnabled(true);
          } else if (filterKey === VariationsFilter.InaccurateParentPages) {
            setIsInaccurateParentPagesEnabled(true);
          }
        }}
        sx={{
          fontSize: '14px',
          fontWeight: '400',
          color: 'inherit',
          textTransform: 'none',
          padding: 0,
          minWidth: 'auto',
          justifyContent: 'flex-start',
          '&:hover': {
            backgroundColor: 'transparent !important',
            textDecoration: 'underline !important',
            cursor: 'pointer !important',
            '& .hover-arrow': {
              transform: 'translateX(5px)'
            }
          },
          '& .hover-arrow': {
            height: '16px !important',
            width: '16px !important',
            minHeight: '16px !important',
            minWidth: '16px !important',
            flexShrink: 0,
            marginLeft: '4px'
          }
        }}
      >
        {subText}
      </TextArrow>
    );
  };

  const generateVariationsGaugeMetricSubtitle = (
    performanceGaugeData: VariationsPerformanceGaugeData,
    filterKey: VariationsFilter
  ) => {
    const item = performanceGaugeData.gaugeData.find((item) => item.filterKey === filterKey);
    if (!item) {
      return <Text sx={{ fontSize: '14px', fontWeight: 400 }}>No data available</Text>;
    }

    let subtitle = '';
    switch (filterKey) {
      case VariationsFilter.ChildVariationRate:
      case VariationsFilter.ChildAccuracy:
      case VariationsFilter.ParentPageAccuracy:
      case VariationsFilter.PageDensity:
        subtitle = generateSingleMetricSubtitle(item, filterKey);
        break;
      case VariationsFilter.SingleSkuPages:
        return generateProductTableFilterSubtitle(item.value, filterKey);
      case VariationsFilter.VariatedSkuPages:
        return generateProductTableFilterSubtitle(item.value, filterKey);
      case VariationsFilter.CorrectChildSkus:
        return generateProductTableFilterSubtitle(item.value, filterKey);
      case VariationsFilter.IncorrectChildSkus:
        return generateProductTableFilterSubtitle(item.value, filterKey);
      case VariationsFilter.InaccurateParentPages:
        return generateProductTableFilterSubtitle(item.value, filterKey);
      default:
        subtitle = 'Data available from other API';
    }

    return <Text sx={{ fontSize: '14px', fontWeight: 400 }}>{subtitle}</Text>;
  };

  const generateVariationPerformanceSubtitle = () => {
    return `The metrics below display the  child variation rate, child SKU accuracy, and parent page accuracy for ${displayName} during the last week period. Child variation accuracy metrics are displayed using the last week's final variation mapping between child and parent SKUs.`;
  };

  const generateGaugeSubtitle = (metricName: string, description?: string) => {
    const baseText = `The ${metricName} gauge shows current performance levels for ${displayName}`;
    return description
      ? `${baseText}. ${description}`
      : `${baseText}, providing an at-a-glance view of key metrics against target thresholds.`;
  };

  const generateVariationsSplineSubtitle = (
    metricName: string,
    primaryLegendMetric?: number,
    percentChange?: number,
    metricType: METRICTYPE = METRICTYPE.PERCENT,
    parentPageCount?: boolean
  ) => {
    // Format the primary metric value
    const formattedPrimaryMetric = metricFormatter(primaryLegendMetric || 0, metricType, { decimalPlaces: 2 });
    // Format the percent change and determine direction
    const formattedPercentChange = metricFormatter(Math.abs(percentChange || 0), METRICTYPE.PERCENT, {
      decimalPlaces: 1
    });
    const direction = (percentChange || 0) >= 0 ? 'higher' : 'lower';

    if (parentPageCount) {
      return `There were ${formattedPrimaryMetric} parent pages for ${displayName} during the ${mainTimePeriodShortDisplayName} time period, which is ${formattedPercentChange} ${direction} compared to the ${comparisonTimePeriodShortDisplayName}.`;
    }

    return `${displayName} earned ${formattedPrimaryMetric} ${metricName.toLowerCase()} during the ${mainTimePeriodShortDisplayName} time period, which is ${formattedPercentChange} ${direction} compared to the ${comparisonTimePeriodShortDisplayName}.`;
  };

  const generateBarChartSubtitle = (metricName: string, groupBy: string) => {
    const formatMetricName = (name: string) => {
      return name
        .toLowerCase()
        .replace(/\bskus\b/gi, 'SKUs')
        .replace(/\bsku\b/gi, 'SKU');
    };

    return `The chart displays the ${formatMetricName(
      metricName
    )} for ${displayName} by ${groupBy.toLocaleLowerCase()} during the ${mainTimePeriodShortDisplayName} time period compared to the ${comparisonTimePeriodShortDisplayName}.`;
  };

  const generatePageSizeSubtitle = () => {
    return `The chart displays the number of child SKUs per page for ${displayName} during the ${mainTimePeriodShortDisplayName} time period.`;
  };

  const generateGridSubtitle = (dataType: string) => {
    if (dataType.toLowerCase().includes('parent sku') || dataType === 'Page Detail by Parent ASIN') {
      return `The table displays the page detail for ${displayName} by Parent SKU during the last week period compared to the ${comparisonTimePeriodShortDisplayName}.`;
    }

    if (dataType.toLowerCase().includes('child sku') || dataType === 'Page Detail by Child SKU') {
      return `The table displays the page detail for ${displayName} by Child SKU during the last week period compared to the ${comparisonTimePeriodShortDisplayName}.`;
    }
  };

  return {
    generateVariationsGaugeMetricSubtitle,
    generateVariationPerformanceSubtitle,
    generateVariationsSplineSubtitle,
    generatePageSizeSubtitle,
    generateGaugeSubtitle,
    generateBarChartSubtitle,
    generateGridSubtitle
  };
};
